/**
 * Checklist completion from GET /documents/session/:id/summary `section_id`s
 * plus the same slots implied by unsynced local uploads.
 */
import {
  CASE_DOCUMENT_SECTION,
  PRIOR_DENIAL_CRIMINAL_DOCUMENT_TYPE,
  mapAsylumDocTypeToSection,
  mapStayTierKeyToSection,
  type CaseDocumentSectionId,
} from "../constants/caseDocumentSections";
import { isIdentityCategoryComplete } from "./identityProgress";

export interface DocumentChecklistHints {
  /** Distinct section_id / document_type values with at least one stored document. */
  uploadedSectionIds: string[];
}

export type DocChecklistCategory =
  | "identity"
  | "residence"
  | "criminal"
  | "asylum"
  | "employment"
  | "family"
  | "other"
  | "denial"
  | "vulnerability";

/** Keys for `document_section_acknowledged` in session + exit modal; must match backend `DocumentSectionAckKey`. */
export type DocumentSectionAckKey =
  | "identity"
  | "residence"
  | "criminal"
  | "asylum"
  | "employment"
  | "family"
  | "vulnerability"
  | "other"
  | "denial";

/**
 * Per-category required Mongo `section_id`s for “complete” (except `residence`; see below).
 * `residence` is intentionally empty here - checklist completion uses the applicant-facing
 * `proofOfResidenceAcknowledged` flag (confirmation modal). Upload evidence drives in-progress styling only.
 * Criminal completion is handled in `getCategoryUploadStatus` (supports legacy `nie_cert` uploads).
 */
export const REQUIRED_SECTIONS: Record<DocChecklistCategory, readonly CaseDocumentSectionId[]> = {
  identity: [CASE_DOCUMENT_SECTION.identity_section],
  residence: [],
  criminal: [CASE_DOCUMENT_SECTION.criminal_record_certificate],
  asylum: [CASE_DOCUMENT_SECTION.asylum_section],
  employment: [],
  family: [CASE_DOCUMENT_SECTION.family_section],
  other: [],
  denial: [CASE_DOCUMENT_SECTION.prior_denial_documents],
  vulnerability: [],
};

/** Sections that count as residence evidence in the current UI.
 * The Residence screen uploads empadronamiento/rental/medical/bank/utility/other
 * into the `bank_statements` section id.
 */
const RESIDENCE_EVIDENCE_SECTIONS: readonly CaseDocumentSectionId[] = [
  CASE_DOCUMENT_SECTION.proof_of_residence_section,
];

/**
 * Residence completion is driven by the residence section bucket.
 */
export function getResidenceUploadStatus(
  sections: Set<string>
): "complete" | "inProgress" | "notStarted" {
  // Product rule: any uploaded residence-supporting doc marks this category complete.
  if (RESIDENCE_EVIDENCE_SECTIONS.some((s) => sections.has(s))) return "complete";
  return "notStarted";
}

/** Form fields that imply an uploaded section before the summary refetches. */
export interface FormChecklistData {
  /** Applicant tapped “Yes” on proof-of-residence exit modal - required for organizer “complete”. */
  proofOfResidenceAcknowledged?: boolean;
  /** Per-section modal confirmation; synced to ``document_section_acknowledged`` in Mongo ``collected_fields``. */
  documentSectionAcknowledged?: Partial<Record<DocumentSectionAckKey, boolean>>;
  documentChecklistHints?: DocumentChecklistHints;
  docType?: string;
  hasPassport?: "" | "yes" | "no";
  passportOcrSkipped?: string;
  dob?: { day?: string; month?: string; year?: string };
  docFullName?: string;
  placeOfBirth?: string;
  countryOfBirth?: string;
  sex?: string;
  passportNumber?: string;
  nationalIdNumber?: string;
  travelDocumentNumber?: string;
  registrationDocumentNumber?: string;
  otherDocumentNumber?: string;
  docUpload?: string;
  identityUploads?: Partial<
    Record<"passport" | "nationalId" | "travelDoc" | "cedulaInscripcion" | "other", string>
  >;
  stayDocuments?: { filename?: string; type: string }[];
  asylumDocUpload?: string;
  asylumDocType?: string;
  asylumDocuments?: { filename?: string }[];
  familyDocuments?: { filename?: string }[];
  employmentDocuments?: { filename?: string; category?: string }[];
  otherDocuments?: { filename?: string }[];
  denialResolutionUpload?: string;
  priorCriminalRecordUpload?: string;
  criminalRecordDocUpload?: string;
  criminalCertificates?: {
    status: string;
    certRequestedNotReceived?: boolean;
  }[];
  countriesOfResidence?: string[];
  vulnerabilityReport?: { filename?: string };
}

export function deriveDocumentChecklistHints(
  docs: { section_id?: string | null; document_type?: string | null }[]
): DocumentChecklistHints {
  const sections = new Set<string>();
  for (const d of docs) {
    const s = String(d.section_id || d.document_type || "").trim();
    if (s) sections.add(s);
  }
  return { uploadedSectionIds: [...sections] };
}

/** Row shape from GET /documents/session/:id/summary */
export interface SessionSummaryDocRow {
  id?: string | null;
  filename?: string | null;
  section_id?: string | null;
  document_type?: string | null;
  supporting_description?: string | null;
  self_employment_activity?: string | null;
  criminal_record_country?: string | null;
}

/** Mirrors FormContext `CriminalCertificate` without importing FormContext (avoid cycles). */
export interface CriminalCertificateSync {
  country: string;
  filename: string;
  status: "pending" | "uploaded" | "notAvailable";
  documentId?: string;
  certRequestedNotReceived?: boolean;
  requestProofFile?: string;
}

export interface AsylumDocumentSync {
  id: string;
  category: string;
  filename: string;
  fileSize: string;
  description: string;
  documentId?: string;
}

export interface EmploymentDocumentSync {
  id: string;
  category: string;
  filename: string;
  fileSize: string;
  description: string;
  documentId?: string;
}

export interface StayDocumentSync {
  id: string;
  filename: string;
  fileSize: string;
  type: string;
  month: string;
  year: string;
  documentId?: string;
}

export interface FamilyDocumentSync {
  id: string;
  category: string;
  filename: string;
  fileSize: string;
  description: string;
  documentId?: string;
}

/**
 * Rebuild countries + per-country certificate rows from session summary.
 * Country is stored server-side as `criminal_record_country` (fallback: supporting_description).
 */
export function mergeCriminalStateFromSessionSummary(
  docs: SessionSummaryDocRow[],
  prevCountries: string[],
  prevCerts: CriminalCertificateSync[]
): { countriesOfResidence: string[]; criminalCertificates: CriminalCertificateSync[] } {
  const byCountry = new Map<string, { filename: string; documentId: string }>();
  for (const d of docs) {
    const sid = String(d.section_id ?? "").trim();
    if (
      sid !== CASE_DOCUMENT_SECTION.criminal_record_certificate &&
      sid !== CASE_DOCUMENT_SECTION.nie_cert
    ) {
      continue;
    }
    const country = String(d.criminal_record_country ?? d.supporting_description ?? "").trim();
    if (!country) continue;
    const fn = String(d.filename ?? "").trim();
    if (!fn) continue;
    const docId = String(d.id ?? "").trim();
    byCountry.set(country, { filename: fn, documentId: docId });
  }

  const countrySet = new Set<string>([...prevCountries, ...byCountry.keys()]);
  const countries = [...countrySet];

  const certs: CriminalCertificateSync[] = countries.map((country) => {
    const server = byCountry.get(country);
    const prev = prevCerts.find((c) => c.country === country);
    if (server) {
      return {
        country,
        filename: server.filename,
        status: "uploaded" as const,
        documentId: server.documentId || undefined,
      };
    }
    if (prev) {
      return { ...prev, country };
    }
    return {
      country,
      filename: "",
      status: "pending" as const,
      certRequestedNotReceived: false,
      requestProofFile: "",
    };
  });

  return { countriesOfResidence: countries, criminalCertificates: certs };
}

export function mergeAsylumStateFromSessionSummary(
  docs: SessionSummaryDocRow[],
  prevDocs: AsylumDocumentSync[]
): AsylumDocumentSync[] {
  const merged: AsylumDocumentSync[] = [];

  for (const d of docs) {
    const sid = String(d.section_id ?? "").trim();
    const dtype = String(d.document_type ?? "").trim();
    if (sid !== CASE_DOCUMENT_SECTION.asylum_section) {
      continue;
    }

    const filename = String(d.filename ?? "").trim();
    if (!filename) {
      continue;
    }

    const description = String(d.supporting_description ?? "").trim();
    const category =
      dtype === "application_receipt_id"
        ? "receipt"
        : dtype === "red_card_id"
          ? "card"
          : dtype === "denial_resolution_id"
            ? "resolution"
            : "other";
    const id =
      String(d.id ?? "").trim() ||
      `asylum-${category}-${filename}-${description || "file"}`;
    const prev =
      prevDocs.find((doc) => doc.id === id) ||
      prevDocs.find(
        (doc) =>
          doc.category === category &&
          doc.filename === filename &&
          doc.description === description
      );

    merged.push({
      id,
      category,
      filename,
      fileSize: prev?.fileSize ?? "",
      description: description || prev?.description || "",
      documentId: (/^[a-fA-F0-9]{24}$/.test(id) ? id : undefined) || prev?.documentId,
    });
  }

  return merged.length > 0 ? merged : prevDocs;
}

export function mergeEmploymentStateFromSessionSummary(
  docs: SessionSummaryDocRow[],
  prevDocs: EmploymentDocumentSync[]
): EmploymentDocumentSync[] {
  const merged: EmploymentDocumentSync[] = [];

  for (const d of docs) {
    const sid = String(d.section_id ?? "").trim();
    if (sid !== CASE_DOCUMENT_SECTION.employment_section) {
      continue;
    }

    const filename = String(d.filename ?? "").trim();
    if (!filename) continue;

    const dtype = String(d.document_type ?? "").trim();
    const description = String(d.supporting_description ?? "").trim();
    const category =
      dtype === "work_contract_id"
        ? "contract"
        : dtype === "nomina_id"
          ? "payslip"
          : dtype === "vida_laboral_id"
            ? "vidalaboral"
            : dtype === "job_offer_letter_id"
              ? "joboffer"
              : dtype === "self_employment_id"
                ? "autonomo"
                : "other";
    const id =
      String(d.id ?? "").trim() ||
      `employment-${category}-${filename}-${description || "file"}`;
    const prev =
      prevDocs.find((doc) => doc.id === id) ||
      prevDocs.find(
        (doc) =>
          doc.category === category &&
          doc.filename === filename &&
          doc.description === description
      );

    merged.push({
      id,
      category,
      filename,
      fileSize: prev?.fileSize ?? "",
      description: description || prev?.description || "",
      documentId: (/^[a-fA-F0-9]{24}$/.test(id) ? id : undefined) || prev?.documentId,
    });
  }

  return merged.length > 0 ? merged : prevDocs;
}

/** Infer employed vs self-employed from uploaded employment rows (Mongo summary → local categories). */
export function inferWorkTypeFromUploadedEmploymentDocs(
  docs: readonly { category?: string; filename?: string }[],
): "" | "employed" | "selfemployed" {
  for (const d of docs) {
    if (!String(d.filename || "").trim()) continue;
    const cat = String(d.category || "").trim();
    if (cat === "autonomo") return "selfemployed";
    if (["contract", "payslip", "vidalaboral", "joboffer", "other"].includes(cat)) return "employed";
  }
  return "";
}

export function mergeStayStateFromSessionSummary(
  docs: SessionSummaryDocRow[],
  prevDocs: StayDocumentSync[]
): StayDocumentSync[] {
  const merged: StayDocumentSync[] = [];

  for (const d of docs) {
    const sid = String(d.section_id ?? "").trim();
    if (sid !== CASE_DOCUMENT_SECTION.proof_of_residence_section) {
      continue;
    }

    const filename = String(d.filename ?? "").trim();
    if (!filename) continue;

    const dtype = String(d.document_type ?? "").trim();
    const type =
      dtype === "padron_municipal_registration_id"
        ? "Padron"
        : dtype === "rental_contract_id"
          ? "Rental"
          : dtype === "medical_record_id"
            ? "Medical"
            : dtype === "childrens_school_enrollment_id"
              ? "School"
              : dtype === "bank_statement_id"
                ? "Bank"
                : dtype === "utility_bill_id"
                  ? "Utility"
                  : dtype === "payslip_work_contract_id"
                    ? "Payslip"
                    : dtype === "money_transfer_receipt_id"
                      ? "Transfer"
                      : dtype === "course_training_certificate_id"
                        ? "Course"
                        : dtype === "transport_ticket_id"
                          ? "Transport"
                          : dtype === "police_report_id"
                            ? "Police"
                            : dtype === "official_letter_notification_id"
                              ? "Official"
                              : "Other";

    const mongoId = String(d.id ?? "").trim();
    const id = mongoId || `stay-${type}-${filename}`;
    const prev = prevDocs.find((doc) => doc.id === id) ||
      prevDocs.find((doc) => doc.type === type && doc.filename === filename);

    merged.push({
      id,
      filename,
      fileSize: prev?.fileSize ?? "",
      type,
      month: prev?.month ?? "",
      year: prev?.year ?? "",
      documentId: mongoId || prev?.documentId,
    });
  }

  return merged.length > 0 ? merged : prevDocs;
}

export function mergeFamilyStateFromSessionSummary(
  docs: SessionSummaryDocRow[],
  prevDocs: FamilyDocumentSync[]
): FamilyDocumentSync[] {
  const merged: FamilyDocumentSync[] = [];

  for (const d of docs) {
    const sid = String(d.section_id ?? "").trim();
    if (sid !== CASE_DOCUMENT_SECTION.family_section) {
      continue;
    }

    const filename = String(d.filename ?? "").trim();
    if (!filename) continue;

    const dtype = String(d.document_type ?? "").trim();
    const description = String(d.supporting_description ?? "").trim();
    const category =
      dtype === "marriage_certificate_id"
        ? "marriage"
        : dtype === "libro_de_familia_id"
          ? "libro"
          : dtype === "childrens_birth_certificates_id"
            ? "birth"
            : dtype === "proof_of_cohabitation_id"
              ? "cohabitation"
              : dtype === "school_enrollment_id"
                ? "school"
                : "other";
    const id =
      String(d.id ?? "").trim() ||
      `family-${category}-${filename}-${description || "file"}`;
    const prev =
      prevDocs.find((doc) => doc.id === id) ||
      prevDocs.find(
        (doc) =>
          doc.category === category &&
          doc.filename === filename &&
          doc.description === description
      );

    merged.push({
      id,
      category,
      filename,
      fileSize: prev?.fileSize ?? "",
      description: description || prev?.description || "",
      documentId: (/^[a-fA-F0-9]{24}$/.test(id) ? id : undefined) || prev?.documentId,
    });
  }

  return merged.length > 0 ? merged : prevDocs;
}

/** Shape matches ``FormContext`` ``OtherDocument`` (organizer / DocUpload "Other"). */
export interface OtherDocumentSync {
  id: string;
  docType: string;
  filename: string;
  fileSize: string;
  description: string;
  documentId?: string;
}

/**
 * Hydrate ``otherDocuments`` from Mongo session summary (e.g. bulk import ``other_supporting``).
 * Skips vulnerability rows (``supporting_description`` = ``vulnerability_report``).
 * Keeps optimistic local rows (non-Mongo ObjectId ids) alongside server rows.
 */
export function mergeOtherDocumentsFromSessionSummary(
  docs: SessionSummaryDocRow[],
  prevDocs: OtherDocumentSync[],
): OtherDocumentSync[] {
  const merged: OtherDocumentSync[] = [];
  const seenMongo = new Set<string>();

  for (const d of docs) {
    const sid = String(d.section_id ?? "").trim();
    const dtype = String(d.document_type ?? "").trim();
    if (dtype !== "other_supporting" && sid !== "other_supporting") {
      continue;
    }
    const supRaw = String(d.supporting_description ?? "").trim();
    if (supRaw.toLowerCase() === "vulnerability_report") {
      continue;
    }
    const filename = String(d.filename ?? "").trim();
    if (!filename) {
      continue;
    }
    const mongoId = String(d.id ?? "").trim();
    const id = /^[a-fA-F0-9]{24}$/.test(mongoId) ? mongoId : `other-${filename}-${mongoId || "row"}`;
    if (/^[a-fA-F0-9]{24}$/.test(mongoId)) {
      seenMongo.add(mongoId);
    }
    const prev =
      prevDocs.find((doc) => doc.documentId === mongoId && mongoId) ||
      prevDocs.find((doc) => doc.id === mongoId && mongoId) ||
      prevDocs.find((doc) => doc.filename === filename && doc.documentId === mongoId);

    merged.push({
      id,
      docType: "otros",
      filename,
      fileSize: prev?.fileSize ?? "",
      description: supRaw || prev?.description || "",
      documentId: /^[a-fA-F0-9]{24}$/.test(mongoId) ? mongoId : prev?.documentId,
    });
  }

  const locals = prevDocs.filter((p) => {
    const mid = String(p.documentId ?? "").trim();
    const pid = String(p.id ?? "").trim();
    const hex = /^[a-fA-F0-9]{24}$/.test(mid) ? mid : /^[a-fA-F0-9]{24}$/.test(pid) ? pid : "";
    if (!hex) {
      return true;
    }
    return !seenMongo.has(hex);
  });

  if (merged.length === 0) {
    return locals;
  }
  return [...merged, ...locals];
}

/**
 * Single vulnerability file (Anexo II) stored as ``other_supporting`` + marker description.
 */
export function mergeVulnerabilityReportFromSessionSummary(
  docs: SessionSummaryDocRow[],
): { filename: string; fileSize: string; documentId?: string } | null {
  for (const d of docs) {
    const sid = String(d.section_id ?? "").trim();
    const dtype = String(d.document_type ?? "").trim();
    if (dtype !== "other_supporting" && sid !== "other_supporting") {
      continue;
    }
    if (String(d.supporting_description ?? "").trim().toLowerCase() !== "vulnerability_report") {
      continue;
    }
    const filename = String(d.filename ?? "").trim();
    if (!filename) {
      continue;
    }
    const mongo = String(d.id ?? "").trim();
    return {
      filename,
      fileSize: "",
      documentId: /^[a-fA-F0-9]{24}$/.test(mongo) ? mongo : undefined,
    };
  }
  return null;
}

/**
 * Map server-side uploaded rows into form filename fields so passport / proof-of-stay
 * UIs show the attached file name again after reload (Mongo is source of truth).
 */
export function deriveUploadFilenamesFromSessionSummary(docs: SessionSummaryDocRow[]): {
  identityUploads?: Partial<
    Record<"passport" | "nationalId" | "travelDoc" | "cedulaInscripcion" | "other", string>
  >;
  identityPassportDocumentId?: string;
  asylumDocUpload?: string;
  denialResolutionUpload?: string;
  denialResolutionDocumentId?: string;
  priorCriminalRecordUpload?: string;
  criminalRecordDocUpload?: string;
} {
  const out: {
    identityUploads?: Partial<
      Record<"passport" | "nationalId" | "travelDoc" | "cedulaInscripcion" | "other", string>
    >;
    identityPassportDocumentId?: string;
    asylumDocUpload?: string;
    denialResolutionUpload?: string;
    denialResolutionDocumentId?: string;
    priorCriminalRecordUpload?: string;
    criminalRecordDocUpload?: string;
  } = {};

  let passportIdentitySummaryDocId: string | undefined;
  let sawPassportIdentityRow = false;

  for (const d of docs) {
    const fn = String(d.filename ?? "").trim();
    if (!fn) continue;
    const sid = String(d.section_id ?? "").trim();
    const dtype = String(d.document_type ?? "").trim();

    if (sid === CASE_DOCUMENT_SECTION.identity_section) {
      const identityKey =
        dtype === "passport_id"
          ? "passport"
          : dtype === "national_id_card_id"
            ? "nationalId"
            : dtype === "travel_document_id"
              ? "travelDoc"
              : dtype === "cedula_de_inscripcion_id"
                ? "cedulaInscripcion"
                : dtype === "other_id"
                  ? "other"
                  : "";
      if (identityKey) {
        out.identityUploads = {
          ...(out.identityUploads || {}),
          [identityKey]: fn,
        };
      }
      if (identityKey === "passport") {
        sawPassportIdentityRow = true;
        const docId = String(d.id ?? "").trim();
        if (docId) passportIdentitySummaryDocId = docId;
      }
      continue;
    }
    if (sid === CASE_DOCUMENT_SECTION.asylum_section) {
      out.asylumDocUpload = fn;
      continue;
    }
    if (sid === CASE_DOCUMENT_SECTION.criminal_record_certificate || sid === CASE_DOCUMENT_SECTION.nie_cert) {
      const countryTag = String(d.criminal_record_country ?? d.supporting_description ?? "").trim();
      if (!countryTag) {
        out.criminalRecordDocUpload = fn;
      }
      continue;
    }
    if (sid === CASE_DOCUMENT_SECTION.prior_denial_documents) {
      if (dtype === PRIOR_DENIAL_CRIMINAL_DOCUMENT_TYPE) {
        out.priorCriminalRecordUpload = fn;
      } else if (dtype === "prior_denial_resolution" || dtype === "prior_denial_stay_proof") {
        out.denialResolutionUpload = fn;
        const mongo = String(d.id ?? "").trim();
        if (/^[a-fA-F0-9]{24}$/.test(mongo)) out.denialResolutionDocumentId = mongo;
      } else if (!out.denialResolutionUpload) {
        out.denialResolutionUpload = fn;
        const mongo = String(d.id ?? "").trim();
        if (/^[a-fA-F0-9]{24}$/.test(mongo)) out.denialResolutionDocumentId = mongo;
      }
    }
  }

  out.identityPassportDocumentId = sawPassportIdentityRow ? passportIdentitySummaryDocId : undefined;

  return out;
}

export function collectLocalUploadedSections(data: FormChecklistData): Set<string> {
  const s = new Set<string>();
  if (String(data.docUpload ?? "").trim()) {
    s.add(CASE_DOCUMENT_SECTION.identity_section);
  }
  for (const d of data.stayDocuments ?? []) {
    if (!String(d.filename ?? "").trim()) continue;
    s.add(mapStayTierKeyToSection(d.type));
  }
  if (String(data.asylumDocUpload ?? "").trim()) {
    s.add(mapAsylumDocTypeToSection(String(data.asylumDocType ?? "")));
  }
  if ((data.asylumDocuments ?? []).some((d) => String(d.filename ?? "").trim())) {
    s.add(CASE_DOCUMENT_SECTION.asylum_section);
  }
  if ((data.familyDocuments ?? []).some((d) => String(d.filename ?? "").trim())) {
    s.add(CASE_DOCUMENT_SECTION.family_section);
  }
  if ((data.otherDocuments ?? []).some((d) => String(d.filename ?? "").trim())) {
    s.add("other_supporting");
  }
  for (const d of data.employmentDocuments ?? []) {
    if (!String(d.filename ?? "").trim()) continue;
    s.add(CASE_DOCUMENT_SECTION.employment_section);
  }
  if (String(data.denialResolutionUpload ?? "").trim()) {
    s.add(CASE_DOCUMENT_SECTION.prior_denial_documents);
  }
  if (String(data.priorCriminalRecordUpload ?? "").trim()) {
    s.add(CASE_DOCUMENT_SECTION.prior_denial_documents);
  }
  if (String(data.criminalRecordDocUpload ?? "").trim()) {
    s.add(CASE_DOCUMENT_SECTION.criminal_record_certificate);
  }
  if (
    (data.criminalCertificates ?? []).some(
      (c) => c.status === "uploaded" || c.status === "notAvailable" || c.certRequestedNotReceived
    )
  ) {
    s.add(CASE_DOCUMENT_SECTION.criminal_record_certificate);
  }
  return s;
}

export function buildMergedUploadedSections(data: FormChecklistData): Set<string> {
  const merged = new Set<string>(data.documentChecklistHints?.uploadedSectionIds ?? []);
  for (const id of collectLocalUploadedSections(data)) {
    merged.add(id);
  }
  return merged;
}

export function isDocumentSectionAcknowledged(
  section: DocumentSectionAckKey,
  data: Pick<FormChecklistData, "documentSectionAcknowledged" | "proofOfResidenceAcknowledged">
): boolean {
  if (section === "residence") {
    return (
      data.documentSectionAcknowledged?.residence === true ||
      data.proofOfResidenceAcknowledged === true
    );
  }
  return data.documentSectionAcknowledged?.[section] === true;
}

/** At least one residence file in session or local form (modal ack alone is not enough). */
function hasResidenceDocumentEvidence(sections: Set<string>, d: FormChecklistData): boolean {
  const fromSections = RESIDENCE_EVIDENCE_SECTIONS.some((s) => sections.has(s));
  const fromStay = (d.stayDocuments ?? []).some((x) => String(x.filename ?? "").trim());
  return fromSections || fromStay;
}

/** Certificate / NIE uploads or a resolved per-country row (uploaded, unavailable, or proof requested). */
function hasCriminalDocumentEvidence(sections: Set<string>, d: FormChecklistData): boolean {
  if (
    sections.has(CASE_DOCUMENT_SECTION.criminal_record_certificate) ||
    sections.has(CASE_DOCUMENT_SECTION.nie_cert)
  ) {
    return true;
  }
  if (String(d.criminalRecordDocUpload ?? "").trim()) return true;
  const certs = d.criminalCertificates ?? [];
  return certs.some(
    (c) =>
      c.status === "uploaded" ||
      c.status === "notAvailable" ||
      Boolean(c.certRequestedNotReceived),
  );
}

function hasAsylumDocumentEvidence(sections: Set<string>, d: FormChecklistData): boolean {
  if (sections.has(CASE_DOCUMENT_SECTION.asylum_section)) return true;
  if (String(d.asylumDocUpload ?? "").trim()) return true;
  return (d.asylumDocuments ?? []).some((x) => String(x.filename ?? "").trim());
}

function hasEmploymentDocumentEvidence(sections: Set<string>, d: FormChecklistData): boolean {
  if (sections.has(CASE_DOCUMENT_SECTION.employment_section)) return true;
  return (d.employmentDocuments ?? []).some((x) => String(x.filename ?? "").trim());
}

function hasFamilyDocumentEvidence(sections: Set<string>, d: FormChecklistData): boolean {
  if (sections.has(CASE_DOCUMENT_SECTION.family_section)) return true;
  return (d.familyDocuments ?? []).some((x) => String(x.filename ?? "").trim());
}

/**
 * "Other" uploads use `other_supporting`. Vulnerability uploads the same section - do not count those
 * as evidence for the generic "other" category.
 */
function hasOtherCategoryDocumentEvidence(sections: Set<string>, d: FormChecklistData): boolean {
  if ((d.otherDocuments ?? []).some((x) => String(x.filename ?? "").trim())) return true;
  if (
    sections.has("other_supporting") &&
    !String(d.vulnerabilityReport?.filename ?? "").trim()
  ) {
    return true;
  }
  return false;
}

function hasDenialDocumentEvidence(sections: Set<string>, d: FormChecklistData): boolean {
  if (sections.has(CASE_DOCUMENT_SECTION.prior_denial_documents)) return true;
  if (String(d.denialResolutionUpload ?? "").trim()) return true;
  if (String(d.priorCriminalRecordUpload ?? "").trim()) return true;
  return false;
}

function hasVulnerabilityDocumentEvidence(d: FormChecklistData): boolean {
  return Boolean(String(d.vulnerabilityReport?.filename ?? "").trim());
}

function hasIdentityNumberForSelectedDoc(data: FormChecklistData): boolean {
  switch (data.docType) {
    case "passport":
      return !!String(data.passportNumber ?? "").trim();
    case "nationalId":
      return !!String(data.nationalIdNumber ?? "").trim();
    case "travelDoc":
      return !!String(data.travelDocumentNumber ?? "").trim();
    case "cedulaInscripcion":
      return !!String(data.registrationDocumentNumber ?? "").trim();
    case "other":
      return !!String(data.otherDocumentNumber ?? "").trim();
    default:
      return false;
  }
}

function hasIdentityDocumentUploaded(sections: Set<string>, data: FormChecklistData): boolean {
  if (sections.has(CASE_DOCUMENT_SECTION.identity_section)) return true;
  if (String(data.docUpload ?? "").trim()) return true;
  const u = data.identityUploads;
  if (!u) return false;
  return (
    !!String(u.passport ?? "").trim() ||
    !!String(u.nationalId ?? "").trim() ||
    !!String(u.travelDoc ?? "").trim() ||
    !!String(u.cedulaInscripcion ?? "").trim() ||
    !!String(u.other ?? "").trim()
  );
}

function hasIdentityProgress(sections: Set<string>, data: FormChecklistData): boolean {
  if (sections.has(CASE_DOCUMENT_SECTION.identity_section)) return true;
  if (String(data.docUpload ?? "").trim()) return true;
  if (data.docType && hasIdentityNumberForSelectedDoc(data)) return true;
  return false;
}

function hasCriminalFlowProgress(sections: Set<string>, data: FormChecklistData): boolean {
  if (
    sections.has(CASE_DOCUMENT_SECTION.criminal_record_certificate) ||
    sections.has(CASE_DOCUMENT_SECTION.nie_cert)
  ) {
    return true;
  }
  const certs = data.criminalCertificates ?? [];
  if (
    certs.some(
      (c) =>
        c.status === "uploaded" ||
        c.status === "notAvailable" ||
        c.certRequestedNotReceived
    )
  ) {
    return true;
  }
  return (data.countriesOfResidence ?? []).length > 0;
}

/**
 * Organizer / document-flow status.
 * - **Identity**: passport requires PDF + required manual fields (see `isIdentityCategoryComplete`);
 *   other ID types need doc number + file. No exit-modal ack.
 * - **All other categories** that use “mark complete”: require both `document_section_acknowledged`
 *   (or residence proof flag) **and** at least one document / substantive evidence in that category.
 *   Ack alone with no uploads shows as **in progress** (not complete).
 */
export function getCategoryUploadStatus(
  cat: DocChecklistCategory,
  sections: Set<string>,
  data?: FormChecklistData
): "complete" | "inProgress" | "notStarted" {
  const d: FormChecklistData = data ?? {};

  if (cat === "residence") {
    const ack = isDocumentSectionAcknowledged("residence", d);
    const hasDoc = hasResidenceDocumentEvidence(sections, d);
    if (ack && hasDoc) return "complete";
    if (hasDoc) return "inProgress";
    if (ack) return "inProgress";
    return "notStarted";
  }

  if (cat === "identity") {
    if (isIdentityCategoryComplete(d)) return "complete";

    const identityStartedLocally =
      d.hasPassport === "yes" ||
      d.hasPassport === "no" ||
      !!(d.docType && String(d.docType).trim()) ||
      hasIdentityDocumentUploaded(sections, d) ||
      hasIdentityProgress(sections, d);

    if (identityStartedLocally) return "inProgress";
    return "notStarted";
  }

  if (cat === "criminal") {
    const ack = isDocumentSectionAcknowledged("criminal", d);
    const hasDoc = hasCriminalDocumentEvidence(sections, d);
    if (ack && hasDoc) return "complete";
    if (hasCriminalFlowProgress(sections, d)) return "inProgress";
    if (ack) return "inProgress";
    return "notStarted";
  }

  if (cat === "vulnerability") {
    const ack = isDocumentSectionAcknowledged("vulnerability", d);
    const hasDoc = hasVulnerabilityDocumentEvidence(d);
    if (ack && hasDoc) return "complete";
    if (hasDoc) return "inProgress";
    if (ack) return "inProgress";
    return "notStarted";
  }

  if (cat === "asylum") {
    const ack = isDocumentSectionAcknowledged("asylum", d);
    const hasDoc = hasAsylumDocumentEvidence(sections, d);
    if (ack && hasDoc) return "complete";
    if (hasDoc) return "inProgress";
    if (ack) return "inProgress";
    return "notStarted";
  }

  if (cat === "employment") {
    const ack = isDocumentSectionAcknowledged("employment", d);
    const hasDoc = hasEmploymentDocumentEvidence(sections, d);
    if (ack && hasDoc) return "complete";
    if (hasDoc) return "inProgress";
    if (ack) return "inProgress";
    return "notStarted";
  }

  if (cat === "family") {
    const ack = isDocumentSectionAcknowledged("family", d);
    const hasDoc = hasFamilyDocumentEvidence(sections, d);
    if (ack && hasDoc) return "complete";
    if (hasDoc) return "inProgress";
    if (ack) return "inProgress";
    return "notStarted";
  }

  if (cat === "other") {
    const ack = isDocumentSectionAcknowledged("other", d);
    const hasDoc = hasOtherCategoryDocumentEvidence(sections, d);
    if (ack && hasDoc) return "complete";
    if (hasDoc) return "inProgress";
    if (ack) return "inProgress";
    return "notStarted";
  }

  if (cat === "denial") {
    const ack = isDocumentSectionAcknowledged("denial", d);
    const hasDoc = hasDenialDocumentEvidence(sections, d);
    if (ack && hasDoc) return "complete";
    if (hasDoc) return "inProgress";
    if (ack) return "inProgress";
    return "notStarted";
  }

  return "notStarted";
}

export function isCategoryUploadComplete(cat: DocChecklistCategory, data: FormChecklistData): boolean {
  const sections = buildMergedUploadedSections(data);
  return getCategoryUploadStatus(cat, sections, data) === "complete";
}

/**
 * Criminal record **certificate** (certificado negativo) in organizer + document flow:
 * Source-of-truth flow always shows the criminal document category and then
 * adapts the guidance inside the page based on the user's situation.
 */
export function shouldShowCriminalRecordDocCategory(hasCriminalRecord: string): boolean {
  return true;
}

export type QualifyingRoute = "work" | "family" | "vulnerability" | "";

export function resolveQualifyingRoute(...values: Array<string | null | undefined>): QualifyingRoute {
  for (const value of values) {
    if (!value) continue;
    if (value === "work" || value === "7A") return "work";
    if (value === "family" || value === "7B") return "family";
    if (value === "vulnerability" || value === "7C") return "vulnerability";
  }
  return "";
}

/**
 * Employment documents are only part of the organizer/doc-flow when the user
 * selected the work regularization route on the eligibility step.
 */
export function shouldShowEmploymentDocCategory(
  qualifyingRoute: string | null | undefined
): boolean {
  return resolveQualifyingRoute(qualifyingRoute) === "work";
}

/**
 * Family documents are only part of the organizer/doc-flow when the user
 * selected the family regularization route on the eligibility step.
 */
export function shouldShowFamilyDocCategory(
  qualifyingRoute: string | null | undefined
): boolean {
  return resolveQualifyingRoute(qualifyingRoute) === "family";
}
